import React from 'react'
import Head from 'next/head'
import axios from 'axios'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import CorporateProductionPage from '../components/design-system/marketing/CorporateProductionPage'
import { corporateProductionFallback } from '../content/seo/corporateEventProduction'
import { compactSeoPublicPayload } from '../utilities/seoPublicPayload'

const getCorporateSeoData = async ({ preview, previewData }) => {
  try {
    const response = await axios.get(`${SERVER_IP}/seo/pages/${preview ? 'preview' : 'published'}`, {
      params: preview
        ? {
            path: corporateProductionFallback.page.path,
            token: previewData?.seoPreviewToken
          }
        : { path: corporateProductionFallback.page.path }
    })

    return compactSeoPublicPayload(response.data.data, {
      source: preview ? 'draft-preview' : 'published',
      customKeys: ['corporateProduction']
    })
  } catch (error) {
    console.warn(`Using source fallback for ${corporateProductionFallback.page.path}: ${error.message}`)
    return compactSeoPublicPayload(corporateProductionFallback, {
      source: 'source-fallback',
      customKeys: ['corporateProduction']
    })
  }
}

export const getStaticProps = wrapper.getStaticProps(
  async ({ preview, previewData }) => {
    const [corporateResponse, reviewResponse, seoData] = await Promise.all([
      axios.request(`${SERVER_IP}/user/static-corporate`),
      axios.get(`${SERVER_IP}/google-review/public`, {
        params: {
          page: corporateProductionFallback.page.path,
          limit: 3
        }
      }).catch(error => {
        console.warn(`Using review fallback for ${corporateProductionFallback.page.path}: ${error.message}`)
        return { data: null }
      }),
      getCorporateSeoData({ preview, previewData })
    ])

    return {
      props: {
        totalEvents: corporateResponse?.data?.total_events || null,
        reviewData: reviewResponse.data,
        seoData,
        isPreview: Boolean(preview)
      },
      revalidate: 3600
    }
  }
)

const mergeCorporateContent = stored => {
  const fallback = corporateProductionFallback.revision.content
  const next = stored?.custom?.corporateProduction || {}
  const mergeObject = key => ({ ...fallback[key], ...(next[key] || {}) })

  const sourceFaqs = next.faqs?.length ? next.faqs : fallback.faqs
  const requiredFaqs = fallback.faqs.filter(faq => faq.key?.startsWith('corporate-procurement-'))
  const faqs = [...sourceFaqs]
  requiredFaqs.forEach(requiredFaq => {
    if (!faqs.some(faq => faq.key === requiredFaq.key || faq.question === requiredFaq.question)) faqs.push(requiredFaq)
  })

  return {
    ...fallback,
    ...next,
    hero: mergeObject('hero'),
    proof: next.proof?.length ? next.proof : fallback.proof,
    paths: {
      ...mergeObject('paths'),
      managed: { ...fallback.paths.managed, ...(next.paths?.managed || {}) },
      planning: { ...fallback.paths.planning, ...(next.paths?.planning || {}) }
    },
    eventTypes: {
      ...mergeObject('eventTypes'),
      items: next.eventTypes?.items?.length ? next.eventTypes.items : fallback.eventTypes.items
    },
    capabilities: {
      ...mergeObject('capabilities'),
      items: next.capabilities?.items?.length ? next.capabilities.items : fallback.capabilities.items
    },
    caseStudies: next.caseStudies?.length ? next.caseStudies : fallback.caseStudies,
    process: {
      ...mergeObject('process'),
      steps: next.process?.steps?.length ? next.process.steps : fallback.process.steps
    },
    venue: {
      ...mergeObject('venue'),
      items: next.venue?.items?.length ? next.venue.items : fallback.venue.items
    },
    procurement: mergeObject('procurement'),
    faqs,
    proposal: mergeObject('proposal'),
    builder: mergeObject('builder'),
    related: {
      ...mergeObject('related'),
      items: next.related?.items?.length ? next.related.items : fallback.related.items
    }
  }
}

const CorporateEventProduction = ({ reviewData, seoData, totalEvents }) => {
  const page = seoData?.page || corporateProductionFallback.page
  const revision = seoData?.revision || corporateProductionFallback.revision
  const hasCorporateComponentContent = Boolean(revision.content?.custom?.corporateProduction)
  const seo = {
    ...corporateProductionFallback.revision.seo,
    ...(hasCorporateComponentContent ? (revision.seo || {}) : {})
  }
  const content = mergeCorporateContent(revision.content)
  const canonical = seo.canonicalUrl || page.canonicalUrl
  const openGraph = {
    type: 'website',
    url: seo.openGraph?.url || canonical,
    title: seo.openGraph?.title || seo.title,
    description: seo.openGraph?.description || seo.description,
    images: seo.openGraph?.images || corporateProductionFallback.revision.seo.openGraph.images,
    site_name: 'Miami Sound Rental'
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${canonical}#service`,
    name: 'Corporate AV and Event Production',
    serviceType: 'Corporate audiovisual and event production',
    url: canonical,
    description: seo.description,
    provider: {
      '@id': 'https://miamisoundrental.com/'
    },
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Corporate event buyers, meeting planners, agencies, venues, and procurement teams'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Corporate audiovisual production capabilities',
      itemListElement: [
        'Audio and communications',
        'Video and presentation systems',
        'Lighting',
        'Staging and scenic support',
        'Technical labor and show operation',
        'Live streaming and recording'
      ].map(name => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name
        }
      }))
    },
    areaServed: [
      { '@type': 'City', name: 'Miami' },
      { '@type': 'City', name: 'Fort Lauderdale' },
      { '@type': 'AdministrativeArea', name: 'South Florida' }
    ]
  }

  return (
    <>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical={canonical}
        noindex={seo.robots?.index === false}
        nofollow={seo.robots?.follow === false}
        twitter={{
          handle: '@miamisoundrent1',
          site: '@miamisoundrent1',
          cardType: seo.twitter?.cardType || 'summary_large_image'
        }}
        openGraph={openGraph}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://miamisoundrental.com/' },
          { position: 2, name: 'Corporate AV Production', item: canonical }
        ]}
      />
      <Head>
        <script
          key='corporate-production-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>
      <CorporateProductionPage content={content} reviewData={reviewData} totalEvents={totalEvents} />
    </>
  )
}

export default CorporateEventProduction
